import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  UserIcon,
  BriefcaseIcon,
  FileCheckIcon,
  UserCheckIcon,
} from "../../Icons";
import { FormProvider } from "react-hook-form";
import EvaluationAutoFill from "./components/EvaluationAutoFill";
import EvaluationCommentSelect from "./components/EvaluationCommentSelect";
import EvaluationInput from "./components/EvaluationInput";
import InputDatePicker from "./components/InputDatePicker";
import EvaluationRadioGroup from "./components/EvaluationRadioGroup";
import EvaluationRatingKey from "./components/EvaluationRatingKey";
import EvaluationSection from "./components/EvaluationSection";
import EvaluationSelect from "./components/EvaluationSelect";
import {
  getRoleConfig,
  POSITION_NAMES,
  PERFORMANCE_COMMENTS,
  TARGETED_GOALS,
} from "./evaluation.constants";
import EvaluationFormHeader from "./components/EvaluationFormHeader";
import { request } from "../../utils/api";
import EvaluationFormFooter from "./components/EvaluationFormFooter";
import { EVALUATION_BTN, EVALUATION_FORM } from "./evaluationTheme";
import { FORM_FIELD } from "../../ui/formTheme";
import { isEmployeeInactiveStatus } from "../../constants/employeeStatus";

const EvaluationUi = ({
  form,
  submitData,
  currentStep,
  onBasicInfoSubmit,
  onCancel,
  selectedRole,
  // Pagination from hook
  currentPage,
  setCurrentPage,
  // Signature from hook
  signatureState,
  openESignModal,
  saveSignature,
  closeESignModal,
  isReadOnly,
  isAdmin,
  isSubmitting = false,
}) => {
  const { watch } = form;

  // Destructure signature state for easier usage
  const { showSignatureModal, reviewerSignatureName } = signatureState || {};

  const watchedEmployeeName = watch("employeeName");
  const watchedReviewerName = watch("reviewerName");
  const watchedStatus = watch("status");
  const watchedEmployeeLabel = watch("employeeLabel");

  // --- State Declarations ---
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const isFormReadOnly = isReadOnly;

  // Dynamic Logic
  const roleConfig = getRoleConfig(selectedRole);
  const roleSections = roleConfig.duties;
  const pageBreakIndex = roleConfig.pageBreakIndex;

  // Dynamic Pages Logic
  let pages = [];
  if (roleConfig.pages) {
    pages = roleConfig.pages;
  } else if (pageBreakIndex) {
    pages = [
      roleSections.slice(0, pageBreakIndex),
      roleSections.slice(pageBreakIndex),
    ];
  } else {
    pages = [roleSections];
  }

  const totalPages = pages.length;
  const currentSections = pages[currentPage - 1] || [];
  const isLastPage = currentPage === totalPages;

  // Helpers for AutoFill
  const getFlattenedItems = (sections) => {
    const flatten = (items) =>
      items.flatMap((item) => {
        const current = [];
        if (item.inputType !== "text") {
          current.push(item);
        }
        if (item.subItems) {
          current.push(...flatten(item.subItems));
        }
        return current;
      });
    return sections.flatMap((section) => flatten(section.items));
  };

  // --- Dynamic User Fetching ---
  const positionMap = {
    PT: "Physical Therapist",
    PTA: "Physical Therapist Assistant",
    RN: "Registered Nurse",
    LPN: "Licensed Practical Nurse",
    HHA: "Home Health Aide",
    OA: "Office Assistant",
    "OFFICE-ASSISTANT": "Office Assistant",
  };

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await request(
          "/users?page=1&limit=100&status=Active",
        );
        if (response.status === 1 && response.result && response.result.users) {
          setUsers(response.result.users);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error("Failed to load users:", error);
        setUsers([]);
      }
    };
    loadUsers();

    try {
      const userStr = localStorage.getItem("currentUser");
      if (userStr) {
        setCurrentUser(JSON.parse(userStr));
      }
    } catch (e) {
      console.error("Error parsing current user", e);
    }
  }, []);

  // Prefill employee name/label when deep-linked from HR Documents (pending evaluation)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const targetUserId = params.get("userId");
    if (!targetUserId || !selectedRole) return;

    const applyEmployee = (match) => {
      if (!match?.id) return;
      const label =
        `${match.firstName || ""} ${match.lastName || ""}`.trim() || match.email;
      form.setValue("employeeName", String(match.id), { shouldValidate: false });
      if (label) {
        form.setValue("employeeLabel", label, { shouldValidate: false });
      }
      if (match.hireDate) {
        form.setValue("hireDate", match.hireDate, { shouldValidate: false });
      }
      if (match.position) {
        form.setValue("jobTitle", match.position, { shouldValidate: false });
      }
    };

    const match = users.find((u) => String(u.id) === String(targetUserId));
    if (match) {
      applyEmployee(match);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await request(`/users/${targetUserId}`);
        if (!cancelled && response.status === 1 && response.result?.user) {
          applyEmployee(response.result.user);
        }
      } catch (error) {
        console.error("Failed to load employee for evaluation prefill:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [users, selectedRole, form]);

  // Auto-fill Reviewer Name if current user is admin
  useEffect(() => {
    if (currentUser?.roleId === 1 && !watchedReviewerName) {
      const adminName =
        `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim();
      if (adminName) {
        form.setValue("reviewerName", adminName, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [currentUser, selectedRole, watchedReviewerName, form]);

  const filteredUserOptions = users
    .filter((user) => {
      if (isEmployeeInactiveStatus(user.status)) return false;
      if (!selectedRole) return true;
      const userRole = user.role?.name;
      return (
        userRole === selectedRole || userRole === positionMap[selectedRole]
      );
    })
    .map((user) => ({
      value: String(user.id),
      label:
        `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email,
    }));

  // Ensure current employee is an option (especially for non-admins with restricted user lists)
  if (
    watchedEmployeeName &&
    watchedEmployeeLabel &&
    !filteredUserOptions.some((o) => o.value === String(watchedEmployeeName))
  ) {
    filteredUserOptions.unshift({
      value: String(watchedEmployeeName),
      label: watchedEmployeeLabel,
    });
  }

  const adminUserOptions = users
    .filter((user) => user.roleId === 1)
    .map((user) => ({
      value: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
      label:
        `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email,
    }));

  // Ensure current admin is an option
  if (currentUser?.roleId === 1) {
    const adminName =
      `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim();
    if (adminName && !adminUserOptions.some((o) => o.value === adminName)) {
      adminUserOptions.unshift({ value: adminName, label: adminName });
    }
  }

  // Helper to get display name from ID
  const getEmployeeNameDisplay = (id) => {
    if (!id) return "";

    // If it's not a numeric ID (e.g., already a name), return it
    if (typeof id === "string" && Number.isNaN(Number(id))) return id;

    const found = users.find((u) => u.id === Number(id));
    if (found) {
      return (
        `${found.firstName || ""} ${found.lastName || ""}`.trim() || found.email
      );
    }

    // BUG FIX: Fallback to the label captured during data loading (important for non-admins)
    if (watchedEmployeeLabel) return watchedEmployeeLabel;

    return id;
  };

  // Auto-fill Hire Date
  useEffect(() => {
    if (watchedEmployeeName && users.length > 0) {
      const selectedUser = users.find(
        (u) => u.id === Number(watchedEmployeeName),
      );
      if (selectedUser && selectedUser.hireDate) {
        const formattedDate = new Date(selectedUser.hireDate)
          .toISOString()
          .split("T")[0];
        form.setValue("hireDate", formattedDate);
      }
    }
  }, [watchedEmployeeName, users, form]);

  return (
    <div className="bg-slate-50">
      <FormProvider {...form}>
        <form
          aria-busy={isSubmitting}
          onSubmit={(e) => {
            e.preventDefault();
            if (currentStep === "BASIC_INFO") {
              onBasicInfoSubmit();
            }
          }}
        >
          {/* --- STEP 2: BASIC INFO FORM --- */}
          {currentStep === "BASIC_INFO" && (
            <div className={EVALUATION_FORM.shell}>
              <div className="mb-6">
                <h2 className={EVALUATION_FORM.pageTitle}>
                  Performance Evaluation
                </h2>
                <p className={EVALUATION_FORM.pageSubtitle}>
                  Provide the employee details to begin the evaluation process.
                </p>
              </div>

              <div className="space-y-4">
                <div className={EVALUATION_FORM.card}>
                  <h3 className={EVALUATION_FORM.cardTitle}>
                    <UserIcon size={18} className={EVALUATION_FORM.cardIcon} />
                    Employee Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <EvaluationSelect
                        label="Employee Name *"
                        name="employeeName"
                        placeholder="Select an employee"
                        options={filteredUserOptions}
                        disabled={isFormReadOnly}
                      />
                    </div>

                    <div>
                      <label className={FORM_FIELD.label}>
                        Job Title/Position *
                      </label>
                      <div className={FORM_FIELD.readonlyBox}>
                        {POSITION_NAMES[selectedRole?.toLowerCase()] ||
                          selectedRole ||
                          "Select Role"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={EVALUATION_FORM.card}>
                  <h3 className={EVALUATION_FORM.cardTitle}>
                    <FileCheckIcon
                      size={18}
                      className={EVALUATION_FORM.cardIcon}
                    />
                    Review Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InputDatePicker
                        label="Evaluation Date *"
                        name="evaluationDate"
                        formatSeparator="/"
                        disabled={isFormReadOnly}
                      />
                    </div>

                    <div>
                      <EvaluationRadioGroup
                        label="Review Type: *"
                        name="reviewType"
                        options={[
                          {
                            value: "Probationary",
                            label: "Probationary Period (3 Mo)",
                          },
                          { value: "Annual", label: "Annual" },
                        ]}
                        disabled={isFormReadOnly}
                      />
                    </div>
                  </div>
                </div>

                {currentUser?.roleId === 1 && (
                  <div className={EVALUATION_FORM.card}>
                    <h3 className={EVALUATION_FORM.cardTitle}>
                      <UserCheckIcon
                        size={18}
                        className={EVALUATION_FORM.cardIcon}
                      />
                      Reviewer Information
                    </h3>
                    <div>
                      <label className={FORM_FIELD.label}>
                        Reviewer Name *
                      </label>
                      <div className={FORM_FIELD.readonlyBox}>
                        {watchedReviewerName || "Reviewer Not Set"}
                      </div>
                    </div>
                  </div>
                )}

                <div className={EVALUATION_FORM.footerRow}>
                  <button
                    type="button"
                    onClick={onCancel}
                    className={`${EVALUATION_BTN.outline} px-5`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onBasicInfoSubmit}
                    className={`${EVALUATION_BTN.primary} px-5`}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* --- STEP 3: EVALUATION FORM --- */}
          {currentStep === "EVALUATION_FORM" && (
            <div className="fixed inset-0 bg-slate-50 z-50 overflow-y-auto">
              <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl border border-indigo-200 my-8">
                <EvaluationFormHeader
                  title="PERFORMANCE EVALUATION"
                  subtitle="Job Duty Performance-Based Evaluation"
                  email={
                    getEmployeeNameDisplay(watchedEmployeeName) ||
                    "Employee Not Selected"
                  }
                  onClose={onCancel}
                />

                <div className="p-8">
                  {/* Info Grid */}
                  <div className={EVALUATION_FORM.metaGrid}>
                    <div className="flex flex-col">
                      <span className={EVALUATION_FORM.metaLabel}>
                        <BriefcaseIcon
                          size={14}
                          className={EVALUATION_FORM.cardIcon}
                        />
                        Position
                      </span>
                      <span className={EVALUATION_FORM.metaValue}>
                        {POSITION_NAMES[selectedRole?.toLowerCase()] ||
                          selectedRole ||
                          "Select Role"}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className={EVALUATION_FORM.metaLabel}>
                        <FileCheckIcon
                          size={14}
                          className={EVALUATION_FORM.cardIcon}
                        />
                        Review Type
                      </span>
                      <div className="mt-1">
                        <EvaluationRadioGroup
                          name="reviewType"
                          options={[
                            {
                              value: "Probationary",
                              label: "Probationary Period (3 Mo)",
                            },
                            { value: "Annual", label: "Annual" },
                          ]}
                          readOnly={isFormReadOnly}
                          disabled={isFormReadOnly}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <span className={EVALUATION_FORM.metaLabel}>
                        <UserIcon
                          size={14}
                          className={EVALUATION_FORM.cardIcon}
                        />
                        Employee Name
                      </span>
                      <span className={EVALUATION_FORM.metaValue}>
                        {getEmployeeNameDisplay(watchedEmployeeName)}
                      </span>
                    </div>
                    {watchedReviewerName && (
                      <div className="flex flex-col">
                        <span className={EVALUATION_FORM.metaLabel}>
                          <UserCheckIcon
                            size={14}
                            className={EVALUATION_FORM.cardIcon}
                          />
                          Reviewer
                        </span>
                        <div className="flex items-center gap-3 mt-1">
                          <div className={EVALUATION_FORM.avatar}>
                            {watchedReviewerName.charAt(0)}
                          </div>
                          <span className="text-sm font-semibold text-slate-800">
                            {watchedReviewerName}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Rating Key */}
                  <div className={`mb-8 ${EVALUATION_FORM.panel}`}>
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                      <div className="flex-1">
                        <h3 className={EVALUATION_FORM.panelTitle}>Key:</h3>
                        <EvaluationRatingKey selectedRole={selectedRole} />
                      </div>
                    </div>
                    {isAdmin && (
                      <EvaluationAutoFill
                        duties={getFlattenedItems(currentSections)}
                        keyPrefix={currentPage}
                        selectedRole={selectedRole}
                      />
                    )}
                  </div>

                  {/* Sections */}
                  {currentSections.length > 0 && (
                    <>
                      {currentSections.map((section, idx) => {
                        let previousItemsCount = 0;
                        for (let i = 0; i < currentPage - 1; i++) {
                          const pageSections = pages[i];
                          previousItemsCount += pageSections.reduce(
                            (acc, s) => acc + s.items.length,
                            0,
                          );
                        }
                        previousItemsCount += currentSections
                          .slice(0, idx)
                          .reduce((acc, s) => acc + s.items.length, 0);

                        const startIndex = previousItemsCount + 1;

                        return (
                          <EvaluationSection
                            key={idx}
                            title={section.title}
                            duties={section.items}
                            startIndex={startIndex}
                            disabled={isFormReadOnly}
                          />
                        );
                      })}

                      {/* Comments */}
                      <div className="mt-6">
                        <h3 className={EVALUATION_FORM.panelTitle}>
                          Comments:
                        </h3>
                        {isAdmin && (
                          <div className="mb-2">
                            <EvaluationCommentSelect
                              targetField={`dutiesCommentPage${currentPage}`}
                              options={PERFORMANCE_COMMENTS}
                              disabled={isFormReadOnly || !isAdmin}
                            />
                          </div>
                        )}
                        <EvaluationInput
                          key={`comment-page-${currentPage}`}
                          type="textarea"
                          name={`dutiesCommentPage${currentPage}`}
                          placeholder="Enter comments..."
                          rows={currentPage === totalPages ? 6 : 4}
                          className="resize-none"
                          disabled={isFormReadOnly}
                        />
                      </div>

                      {/* Goals */}
                      {roleConfig.hasTargetedGoals && (
                        <div className="mt-6">
                          <h3 className={EVALUATION_FORM.panelTitle}>
                            {currentPage === totalPages
                              ? "Targeted Goals For Next Review Cycle:"
                              : "Targeted Goals:"}
                          </h3>
                          {isAdmin && (
                            <div className="mb-2">
                              <EvaluationCommentSelect
                                targetField={`targetedGoalsPage${currentPage}`}
                                options={TARGETED_GOALS}
                                placeholder="Select a targeted goal..."
                                disabled={isFormReadOnly || !isAdmin}
                              />
                            </div>
                          )}
                          <EvaluationInput
                            key={`goal-page-${currentPage}`}
                            type="textarea"
                            name={`targetedGoalsPage${currentPage}`}
                            placeholder="Enter targeted goals..."
                            rows={4}
                            className="resize-none"
                            disabled={isFormReadOnly}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Dates */}
                  {isLastPage && (
                    <div className="grid grid-cols-2 gap-6 mt-4">
                      <div>
                        <label className={FORM_FIELD.label}>Hire Date:</label>
                        <InputDatePicker
                          name="hireDate"
                          formatSeparator="/"
                          disabled={true}
                        />
                        <p className="text-xs text-emerald-600 mt-1">
                          ✓ Auto-filled from employee data
                        </p>
                      </div>

                      <div>
                        <label className={FORM_FIELD.label}>
                          Evaluation Date:
                        </label>
                        <InputDatePicker
                          name="evaluationDate"
                          formatSeparator="/"
                          disabled={isFormReadOnly}
                        />
                      </div>
                    </div>
                  )}

                  <EvaluationFormFooter
                    page={currentPage}
                    setPage={setCurrentPage}
                    totalPages={totalPages}
                    methods={form}
                    onSave={(data) => submitData(data, "save")}
                    onSubmit={(data) => submitData(data, "completed")}
                    isSignatureModalOpen={showSignatureModal}
                    openSignatureModal={() => openESignModal(false)}
                    closeSignatureModal={closeESignModal}
                    handleSignatureApply={saveSignature}
                    signerName={reviewerSignatureName}
                    reviewedBySignedBy={watch("reviewerName")}
                    reviewedBySignature={watch("reviewerSignature")}
                    reviewedBySignedAt={watch("reviewerSignedAt")}
                    adminSignatureDateField="reviewerSignedAt"
                    isReadOnly={isFormReadOnly}
                    currentStatus={watchedStatus}
                  />
                </div>
              </div>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

EvaluationUi.propTypes = {
  form: PropTypes.object.isRequired,
  submit: PropTypes.func,
  submitData: PropTypes.func,
  currentStep: PropTypes.string,
  onBasicInfoSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  selectedRole: PropTypes.string,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  signatureState: PropTypes.object,
  openESignModal: PropTypes.func,
  saveSignature: PropTypes.func,
  closeESignModal: PropTypes.func,
  isReadOnly: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isSubmitting: PropTypes.bool,
};

export default EvaluationUi;